import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const UgandaPage = () => {
  const ugandaData = {
    name: 'Uganda',
    region: 'Africa',
    color: '#606090',
    metaTitle: 'Pharma & Nutraceutical Registration in Uganda | NDA | CTD Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Uganda. NDA requirements, CTD dossier guidelines, timelines, documentation, and regulatory pathways in a growing East African market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Uganda',
      subtitle: 'A high-demand, import-dependent market regulated by Uganda\'s National Drug Authority (NDA), with CTD dossier requirements and strong need for chronic therapies and nutritional supplements.',
      cta: 'Start Your Uganda Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~49M' },
      { label: 'Income Level', value: 'Low-income' },
      { label: 'Health Expenditure per Capita', value: '~USD 35-40' },
      { label: 'Health Expenditure (% of GDP)', value: '~6-7%' },
      { label: 'Urbanisation', value: '~26% urban' },
      { label: 'Life Expectancy', value: '64 (men), 68 (women)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: High Import Dependence + Donor-Driven',
      stats: [
        'Uganda imports the majority of its finished pharmaceuticals - primarily from India, Kenya, China, and Europe',
        'India is consistently among Uganda\'s top 2-3 pharma suppliers, especially in essential generics',
        'Government tenders through National Medical Stores (NMS)',
        'Donor-funded programs (Global Fund, PEPFAR, UNICEF)',
        'Private retail pharmacies in Kampala, Entebbe, Jinja, Mbarara',
        'Nutraceutical demand increasing for malnutrition, maternal health, paediatric supplementation'
      ],
      opportunities: [
        'Ideal for low-cost essentials + chronic disease therapies',
        'Structured nutraceutical products',
        'Strong acceptance of Indian generics',
        'Stable, recurring-demand market for compliant exporters'
      ]
    },
    
    diseaseburden: {
      title: 'What Drives Pharma & Nutra Demand in Uganda',
      subtitle: 'Uganda faces high infectious disease burden with rising NCDs and significant maternal-child health needs.',
      categories: [
        {
          name: 'Infectious Diseases',
          items: [
            'HIV prevalence: ~5.2% (one of the highest in East Africa)',
            'TB incidence: ~196 per 100,000 people',
            'Malaria: Leading cause of outpatient visits (~5% of global cases)',
            'Diarrheal diseases common in children',
            'Portfolio: Antibiotics, antimalarials, ARVs, ORS, zinc, paediatric formulations'
          ]
        },
        {
          name: 'Rising Non-Communicable Diseases',
          items: [
            'Hypertension: Affects ~26% of adults in urban centres',
            'Diabetes: Prevalence ~1.4-2% (rising)',
            'Cardiovascular risk increasing with lifestyle shifts',
            'Portfolio: Antihypertensives, antidiabetics, lipid-lowering agents',
            'Nutra: Omega-3, CoQ10, heart-health supplements'
          ]
        },
        {
          name: 'Maternal & Paediatric Health',
          items: [
            'High rates of anaemia (women 15-49)',
            'Maternal undernutrition widespread',
            'Childhood stunting above 25%',
            'Portfolio: Iron + folic acid, calcium + vitamin D',
            'Prenatal multivitamins, paediatric syrups, dewormers'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'National Drug Authority (NDA)',
      description: 'Ministry of Health, Uganda. All medicines & many health products require NDA registration. Uganda uses the EAC CTD (East African Community Common Technical Document). NDA conducts pharmaceutical quality verification, including sampling and lab testing.',
      pharma: {
        timeline: 'Varies based on dossier completeness and NDA evaluation',
        requirements: [
          'EAC CTD format (Modules 1-5)',
          'Module 1 - Uganda-specific administrative docs',
          'Modules 2-5 - Quality, safety, efficacy documentation',
          'Stability data for tropical climate conditions',
          'Pharmaceutical samples for laboratory evaluation',
          'Indian GMP accepted (NDA may conduct desk audits)',
          'Product information, specifications, impurities documentation',
          'Marketing Authorization with annual retention fees',
          'Periodic PSURs for certain categories'
        ],
        insight: 'Uganda uses EAC CTD format. NDA queries cover quality specs, impurities, bioequivalence, product information, stability, and GMP gaps. Local agent/MAH is mandatory.'
      },
      nutra: {
        timeline: 'Depends on classification and documentation quality',
        requirements: [
          'Full formula (quantitative)',
          'COA & microbial limits',
          'GMP/quality certification for manufacturer',
          'Clear classification (supplement vs medicine)',
          'Safety evidence for certain ingredients',
          'Labelling rules (English mandatory)',
          'Claims must avoid disease-treatment references',
          'CFS/Free Sale Certificate often required'
        ],
        insight: 'Uganda regulates supplements as foods, nutraceuticals, or borderline products depending on claims and composition. Nutraceuticals for immunity, metabolic health, joint care, and kids nutrition fit Uganda\'s public health profile.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration (CTD Format)',
      steps: [
        'Local MAH / Importer Appointment - Uganda-licensed company with NDA authorization',
        'Dossier Submission - EAC CTD with Module 1 (Uganda-specific) and Modules 2-5',
        'Stability data for tropical climate conditions',
        'Pharmaceutical Sample Submission - NDA may require samples for lab evaluation',
        'GMP Requirements - Indian GMP accepted, NDA may conduct desk audits',
        'Evaluation & Queries - Quality specs, impurities, bioequivalence, product info, stability',
        'Marketing Authorization approval - Valid for defined period',
        'Annual retention fees payment',
        'Periodic PSURs for certain categories'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Health Supplement Registration',
      steps: [
        'Category determination (supplement vs medicine)',
        'Full formula (quantitative) preparation',
        'COA & microbial limits documentation',
        'GMP/quality certification for manufacturer',
        'Clear classification to avoid disease-treatment references',
        'Safety evidence for certain ingredients',
        'Labelling in English with compliant claims',
        'CFS/Free Sale Certificate submission',
        'NDA evaluation and classification review',
        'Marketing authorization or approval issuance'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals': [
        'NDA application form',
        'EAC CTD Modules 1-5',
        'GMP certificate',
        'CoPP (if available)',
        'Finished product specs + COA',
        'API documentation / DMF section',
        'Stability (Zone IVb preferred)',
        'Labels, inserts (English)',
        'Samples (requested as needed)'
      ],
      'Nutraceuticals / Supplements': [
        'Category determination note',
        'Complete formulation',
        'COA & safety data',
        'GMP certification',
        'Finished product labelling',
        'CFS/CoH (country of origin certificate)',
        'Claims justification'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Uganda Should Be Part of Your Africa Portfolio',
      reasons: [
        'Strong import reliance - limited domestic manufacturing',
        'High donor-driven pharmaceutical procurement (ARVs, TB, paediatrics)',
        'Growing NCD burden - cardio-diabetes, metabolic health, preventive care',
        'High paediatric & maternal health demand',
        'Local agents actively seek compliant Indian partners',
        'EAC harmonization - alignment with Kenya/Tanzania dossier formats',
        'Stable, recurring-demand market',
        'Strong acceptance of Indian essential generics'
      ],
      bestPortfolio: [
        'Anti-infectives, cardiometabolic therapies, paediatric syrups & ORS',
        'Vitamins, minerals, prenatal & kids nutrition',
        'Immunity boosters, probiotics, omega-3'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Succeed in Uganda',
      services: [
        'Market fit analysis for Uganda',
        'EAC CTD dossier preparation & adaptation',
        'Supplement classification + documentation',
        'NDA artwork & labelling compliance',
        'Ingredient acceptability & safety review',
        'Importer coordination',
        'Query handling, renewals & variations'
      ]
    },
    
    faqs: [
      {
        question: 'What dossier format does Uganda use?',
        answer: 'Uganda uses the EAC CTD format for pharmaceuticals.'
      },
      {
        question: 'Is Indian GMP accepted?',
        answer: 'Yes, Indian GMP is accepted with supporting documentation. NDA may conduct desk audits or require clarification.'
      },
      {
        question: 'What languages must labels use?',
        answer: 'English is mandatory for all medicines and supplements.'
      },
      {
        question: 'Do supplements require registration?',
        answer: 'Yes. All imported supplements must be registered or approved through NDA\'s classification pathways.'
      },
      {
        question: 'Do samples need to be submitted?',
        answer: 'NDA may request samples for post-market quality or during evaluation.'
      }
    ]
  };

  return <CountryTemplate countryData={ugandaData} />;
};
